/**
 * Data contract of the visual workflow editor. Deliberately identical to the
 * one the old form-list `WorkflowStepsEditor` exported — the backend shape
 * (`SubmittalWorkflowStepInput[]`, see src/graphql/submittals/lifecycle.ts) is
 * frozen, this module only changes how it is presented.
 */
export interface WorkflowStepDraft {
  key: string
  name: string
  durationDays: number | null
  reviewerIds: string[]
}

export interface WorkflowFlowMember {
  id: string
  name: string
  email?: string
}

/** Maps drafts (local `key`, UI-only) onto the `SubmittalWorkflowStepInput[]`
 *  shape the backend expects (`index`, no `key`) — see
 *  src/graphql/submittals/lifecycle.ts. */
export const draftsToInput = (steps: WorkflowStepDraft[]) =>
  steps.map((s, index) => ({ index, name: s.name, durationDays: s.durationDays, reviewerIds: s.reviewerIds }))

/** Reverse of draftsToInput — rehydrates drafts from a stored/copied plan
 *  (template.steps or a revision's workflowPlan.steps), minting a fresh
 *  local `key` per row since the plan itself carries none. */
export const draftsFromPlan = (
  plan: { name: string; durationDays: number | null; reviewerIds: string[] }[],
): WorkflowStepDraft[] =>
  plan.map((s) => ({
    key: crypto.randomUUID(),
    name: s.name,
    durationDays: s.durationDays,
    reviewerIds: s.reviewerIds,
  }))

export const createStepDraft = (): WorkflowStepDraft => ({
  key: crypto.randomUUID(),
  name: '',
  durationDays: null,
  reviewerIds: [],
})

/** Reviewer ids that appear in more than one step — the backend rejects those
 *  (validateWorkflowSteps in src/graphql/submittals/workflow.ts), so both the
 *  node badges and the side panel flag them. */
export const duplicateReviewerIdsOf = (steps: WorkflowStepDraft[]): Set<string> => {
  const seen = new Set<string>()
  const dupes = new Set<string>()
  steps.forEach((step) => {
    step.reviewerIds.forEach((id) => {
      if (seen.has(id)) dupes.add(id)
      seen.add(id)
    })
  })
  return dupes
}

/** Same shape check the backend's validateTemplateShape enforces (see
 *  src/graphql/submittals/workflow.ts) — at least one named step and no
 *  reviewer repeated across steps. Steps MAY be unstaffed: a template is a
 *  skeleton, staffing happens when the plan is applied (Freelo #31947103).
 *  Mirrored here (rather than imported) so Save can gate without a round
 *  trip; the backend re-validates regardless. */
export const templateShapeHasIssues = (steps: WorkflowStepDraft[]): boolean => {
  if (steps.length === 0) return true
  const seen = new Set<string>()
  for (const step of steps) {
    if (!step.name.trim()) return true
    for (const id of step.reviewerIds) {
      if (seen.has(id)) return true
      seen.add(id)
    }
  }
  return false
}

/** Submit gate: shape rules PLUS every step staffed — mirrors the backend's
 *  validateWorkflowSteps. */
export const workflowPlanHasIssues = (steps: WorkflowStepDraft[]): boolean =>
  templateShapeHasIssues(steps) || steps.some((step) => step.reviewerIds.length === 0)

export const stepHasIssues = (
  step: WorkflowStepDraft,
  duplicates: Set<string>,
  requireReviewers: boolean = true,
): boolean =>
  !step.name.trim() ||
  (requireReviewers && step.reviewerIds.length === 0) ||
  step.reviewerIds.some((id) => duplicates.has(id))

/** Two letters for the reviewer avatar chips on a step node. */
export const initialsOf = (name?: string | null): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

interface FolderNodeLike {
  id: string
  name: string
  children?: FolderNodeLike[] | null
}

/** Depth-first name lookup in a `docFolderTree` result — the publish node shows
 *  the folder NAME, but both the workflow template and a revision's stored
 *  workflowPlan only carry `targetFolderId`. */
export const findFolderName = (folders: FolderNodeLike[] | null | undefined, id: string | null): string | null => {
  if (!id || !folders) return null
  for (const folder of folders) {
    if (folder.id === id) return folder.name
    const nested = findFolderName(folder.children, id)
    if (nested) return nested
  }
  return null
}
